import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import Button from '../components/Button';
import AddEditPatientModal from '../components/AddEditPatientModal';
import { StatusBadge } from '../config/patientStatus';

function formatDOB(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
}

export default function PatientsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const params = new URLSearchParams(location.search);
  const urlSearch = params.get('search') || '';
  const urlProvider = params.get('provider') || '';
  const urlInactive = params.get('inactive') === 'true';

  const [patients, setPatients] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState(urlSearch);
  const [providerFilter, setProviderFilter] = useState(urlProvider);
  const [showInactive, setShowInactive] = useState(urlInactive);
  const [noResults, setNoResults] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    api.get('/patients/providers').then(res => {
      setProviders(res.data);
    }).catch(() => { });
  }, []);

  const fetchPatients = useCallback(async (search, provider, inactive) => {
    setLoading(true);
    setNoResults(false);

    try {
      const queryParams = new URLSearchParams();
      if (search) queryParams.set('search', search);
      if (provider) queryParams.set('provider_id', provider);

      if (inactive) {
        queryParams.set('statuses', [
          'esrd_hd', 'esrd_hhd', 'esrd_pd', 'esrd_hospice',
          'inactive_lost', 'inactive_transferred', 'inactive_discharged',
          'inactive_moved', 'deceased',
        ].join(','));
      }

      queryParams.set('limit', '200');

      const res = await api.get(`/patients/?${queryParams}`);

      if (res.data.length > 0) {
        setPatients(res.data);
      } else {
        setPatients([]);
        setNoResults(search ? true : false);
      }
    } catch (err) {
      setPatients([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setSearchInput(urlSearch);
    setProviderFilter(urlProvider);
    setShowInactive(urlInactive);
    fetchPatients(urlSearch, urlProvider, urlInactive);
  }, [urlSearch, urlProvider, urlInactive, fetchPatients]);

  const applyFilters = (search, provider, inactive) => {
    const p = new URLSearchParams();
    if (search) p.set('search', search);
    if (provider) p.set('provider', provider);
    if (inactive) p.set('inactive', 'true');
    navigate(`/patients?${p.toString()}`, { replace: true });
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    applyFilters(searchInput, providerFilter, showInactive);
  };

  const handleProviderChange = (val) => {
    setProviderFilter(val);
    applyFilters(searchInput, val, showInactive);
  };

  const handleInactiveToggle = (checked) => {
    setShowInactive(checked);
    applyFilters(searchInput, providerFilter, checked);
  };

  const handleClearFilters = () => {
    setSearchInput('');
    setProviderFilter('');
    setShowInactive(false);
    navigate('/patients', { replace: true });
  };

  const canAddPatient = ['provider', 'admin', 'clinical_staff'].includes(user?.role);
  const hasActiveFilters = urlSearch || urlProvider || urlInactive;

  return (
    <div>
      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>Patients</h1>
      </div>

      <div style={styles.filterBarOuter}>
        <div style={styles.filterBarRow}>
          <form onSubmit={handleSearchSubmit} style={styles.searchForm}>
            <div style={styles.searchWrapper}>
              <span style={styles.searchIcon}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </span>
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by name, MRN, or DOB..."
                style={styles.searchInput}
              />
            </div>
          </form>

          <Button onClick={handleSearchSubmit}>
            Search
          </Button>

          <select
            value={providerFilter}
            onChange={(e) => handleProviderChange(e.target.value)}
            style={styles.filterSelect}
          >
            <option value="">All Providers</option>
            {providers.map(p => (
              <option key={p.id} value={p.id}>
                {p.last_name}, {p.first_name}{p.credential ? ` ${p.credential}` : ''}
              </option>
            ))}
          </select>

          {hasActiveFilters && (
            <Button variant="secondary" onClick={handleClearFilters}>
              Clear filters
            </Button>
          )}

          {canAddPatient && (
            <Button onClick={() => setShowAddModal(true)} style={styles.addButton}>
              + Add Patient
            </Button>
          )}
        </div>

        <div style={styles.checkboxRow}>
          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => handleInactiveToggle(e.target.checked)}
              style={styles.checkbox}
            />
            Search Inactive Patients
          </label>
        </div>
      </div>

      {!loading && !noResults && (
        <div style={styles.countLine}>
          {patients.length} patient{patients.length !== 1 ? 's' : ''}
          {hasActiveFilters ? ' matching filters' : ''}
        </div>
      )}

      <div style={styles.tableWrapper}>
        {loading ? (
          <div style={styles.stateCenter}>Loading patients...</div>
        ) : noResults ? (
          <div style={styles.noResults}>
            <div style={styles.noResultsText}>No patients found.</div>
            {canAddPatient && (
              <Button onClick={() => setShowAddModal(true)}>
                + Add New Patient
              </Button>
            )}
          </div>
        ) : patients.length === 0 && !hasActiveFilters ? (
          <div style={styles.stateCenter}>
            <div style={styles.noResultsText}>No patients in registry yet.</div>
            {canAddPatient && (
              <Button onClick={() => setShowAddModal(true)} style={{ marginTop: '12px' }}>
                + Add First Patient
              </Button>
            )}
          </div>
        ) : (
          <table style={styles.table}>
            <colgroup>
              <col style={{ width: '40%' }} />
              <col style={{ width: '15%' }} />
              <col style={{ width: '20%' }} />
              <col style={{ width: '25%' }} />
            </colgroup>
            <thead>
              <tr>
                {['Name', 'MRN', 'Date of Birth', 'Provider'].map(h => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {patients.map(patient => (
                <tr
                  key={patient.id}
                  style={styles.tr}
                  onClick={() => navigate(`/patients/${patient.id}`)}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--color-accent-wash)'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}
                >
                  <td style={styles.tdName}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                      <span>
                        {patient.last_name}, {patient.first_name}
                        {patient.middle_initial ? ` ${patient.middle_initial}.` : ''}
                        {patient.suffix ? ` ${patient.suffix}` : ''}
                      </span>
                      {patient.current_status !== 'active' && (
                        <StatusBadge status={patient.current_status} />
                      )}
                    </div>
                  </td>
                  <td style={styles.td}>{patient.mrn}</td>
                  <td style={styles.td}>{formatDOB(patient.date_of_birth)}</td>
                  <td style={styles.td}>
                    {providers.find(p => p.id === patient.provider_id)
                      ? (() => { const p = providers.find(pr => pr.id === patient.provider_id); return `${p.last_name}, ${p.first_name}${p.credential ? ` ${p.credential}` : ''}`; })()
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showAddModal && (
        <AddEditPatientModal
          providers={providers}
          onClose={() => setShowAddModal(false)}
          onSaved={(newPatientId) => {
            setShowAddModal(false);
            if (newPatientId) {
              navigate(`/patients/${newPatientId}`);
            } else {
              fetchPatients(urlSearch, urlProvider, urlInactive);
            }
          }}
        />
      )}
    </div>
  );
}

const styles = {
  pageHeader: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', marginBottom: '16px',
  },
  pageTitle: {
    fontSize: 'var(--font-size-xl)', fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)', margin: 0, fontFamily: 'var(--font-family-base)',
  },
  filterBarOuter: { marginBottom: '12px', maxWidth: '1000px' },
  filterBarRow: { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' },
  addButton: { marginLeft: 'auto' },
  searchForm: { flex: '1', minWidth: '200px', maxWidth: '340px' },
  searchWrapper: { position: 'relative', display: 'flex', alignItems: 'center' },
  searchIcon: { position: 'absolute', left: '9px', color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center', pointerEvents: 'none' },
  searchInput: {
    width: '100%', paddingLeft: '30px', paddingRight: '10px',
    paddingTop: '7px', paddingBottom: '7px', fontSize: '13px',
    border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)',
    outline: 'none', boxSizing: 'border-box', backgroundColor: 'var(--color-bg-card)',
    color: 'var(--color-text-primary)', fontFamily: 'var(--font-family-base)',
  },
  filterSelect: {
    padding: '7px 10px', fontSize: '13px', border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-sm)', outline: 'none', backgroundColor: 'var(--color-bg-card)',
    color: 'var(--color-text-primary)', cursor: 'pointer', fontFamily: 'var(--font-family-base)',
  },
  checkboxRow: { marginTop: '7px', paddingLeft: '1px' },
  checkboxLabel: {
    display: 'flex', alignItems: 'center', gap: '6px', fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-muted)', cursor: 'pointer', userSelect: 'none',
    fontFamily: 'var(--font-family-base)',
  },
  checkbox: { cursor: 'pointer', accentColor: 'var(--color-primary)', width: '13px', height: '13px' },
  countLine: { fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginBottom: '8px', fontFamily: 'var(--font-family-base)' },
  tableWrapper: {
    backgroundColor: 'var(--color-bg-card)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    overflow: 'hidden',
    maxWidth: '1000px',
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-table)', tableLayout: 'fixed' },
  th: {
    textAlign: 'left', padding: '10px 16px', fontSize: '11px',
    fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-muted)',
    textTransform: 'uppercase', letterSpacing: '0.05em',
    borderBottom: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg-subtle)',
    fontFamily: 'var(--font-family-base)',
  },
  tr: { borderBottom: '1px solid var(--color-subNav)', cursor: 'pointer', transition: 'background-color 0.1s' },
  tdName: { padding: '12px 16px', color: 'var(--color-text-primary)', fontWeight: 'var(--font-weight-medium)', fontFamily: 'var(--font-family-base)' },
  td: { padding: '12px 16px', color: 'var(--color-text-primary)', fontFamily: 'var(--font-family-base)' },
  stateCenter: { padding: '48px 24px', textAlign: 'center', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', fontFamily: 'var(--font-family-base)' },
  noResults: { padding: '48px 24px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' },
  noResultsText: { fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', fontFamily: 'var(--font-family-base)' },
};